import { Location } from '@angular/common';
import { Component, Input, NgModuleRef, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { AuditLogModalService } from '@bayclubs/audit-log-viewer';
import { HorizonToastService } from '@bayclubs/horizon';
import { ConfirmationService, ModalService, TimeService } from '@bayclubs/utils';

import { GroupClassDetailsModel, InstructorModel } from '@bayclubs/group-classes-employee-api';
import { GlobalEventsService, OnAddEditGroupClass } from '../../../../infrastructure/infrastructure.module';
import { gcManagementEnumerations } from '../../../../interfaces/interfaces.module';
import { GroupClassesApiService, SubBoardApiService } from '../../../../shared/shared.module';
import { InstructorSubstitutionComponent } from '../../../components/instructor-substitution/instructor-substitution.component';
import { RecurringClassActionConfirmationComponent } from '../../../components/recurring-class-action-confirmation/recurring-class-action-confirmation.component';
import { RemoveClassService } from '../../../services/remove-class.service';

@Component({
    selector: '[app-group-classes-details-settings]',
    templateUrl: './group-classes-details-settings.component.html',
    styleUrls: ['./group-classes-details-settings.component.scss'],
    standalone: false
})
export class GroupClassesDetailsSettingsComponent implements OnInit {
    @Input()
    public model: GroupClassDetailsModel;

    @Input()
    public hasAttendens = false;

    private cannotDeletePermanent = false;

    constructor(
        private globalEvents: GlobalEventsService,
        private api: GroupClassesApiService,
        private subBoardApi: SubBoardApiService,
        private confirmationService: ConfirmationService,
        private translate: TranslateService,
        private toast: HorizonToastService,
        private location: Location,
        private modal: ModalService,
        private moduleRef: NgModuleRef<any>,
        private timeService: TimeService,
        private modalService: ModalService,
        private removeClassService: RemoveClassService,
        private auditLogService: AuditLogModalService
    ) {}

    ngOnInit() {}

    public edit(): void {
        this.subBoardApi.getOpenRequest(this.model.club.id, this.model.id).subscribe(response => {
            const hasOpenSubstitutionRequest = response != null;

            if (hasOpenSubstitutionRequest || this.hasAttendens || this.model.hasAlreadyStarted) {
                const bodyText: string = this.translate.instant('GroupClassDetails.EditConfirmationText');

                const headerText: string = this.translate.instant('GroupClassDetails.EditConfirmationTitle');

                const params = {
                    event,
                    warningBar: false,
                    bodyText: bodyText,
                    headerText: headerText,
                    confirmed: (includeFollowing: boolean) => {
                        this.globalEvents.onAddEditGroupClass$.emit(new OnAddEditGroupClass(this.model.id));
                    }
                };

                this.modalService
                    .create<RecurringClassActionConfirmationComponent>(
                        RecurringClassActionConfirmationComponent,
                        params,
                        this.moduleRef,
                        '#confirmationModal'
                    )
                    .subscribe(() => {});
            } else {
                this.globalEvents.onAddEditGroupClass$.emit(new OnAddEditGroupClass(this.model.id));
            }
        });
    }

    public substitute(): void {
        this.subBoardApi.getOpenRequest(this.model.club.id, this.model.id).subscribe(response => {
            const hasOpenSubstitutionRequest = response != null;

            if (hasOpenSubstitutionRequest) {
                this.confirmationService
                    .show(
                        this.translate.instant('GroupClassDetails.SubstitutionRequestExistsSubstituteConfirmationTitle'),
                        this.translate.instant('GroupClassDetails.SubstitutionRequestExistsSubstituteConfirmationText'),
                        this.translate.instant('General.Yes'),
                        this.translate.instant('General.No')
                    )
                    .subscribe(result => {
                        if (result == true) {
                            this.executeSubstitute();
                        }
                    });
            } else {
                this.executeSubstitute();
            }
        });
    }

    private executeSubstitute(): void {
        let params = {
            instructor: this.model.instructor,
            club: this.model.club
        };

        this.modal
            .create<InstructorSubstitutionComponent>(InstructorSubstitutionComponent, params, this.moduleRef)
            .subscribe(result => {
                if (result && result.selectedSubstituteInstructor) {
                    this.confirmSubstitution(result.selectedSubstituteInstructor);
                }
            });
    }

    public confirmSubstitution(selectedSubstituteInstructor: InstructorModel) {
        const substitute = selectedSubstituteInstructor;
        this.api.substitute(this.model.id, selectedSubstituteInstructor.id).subscribe(() => {
            this.showOperationSuccess();
            this.model.substituteInstructor = substitute;
        });
    }

    public cancelSubstitution(): void {
        this.confirmationService
            .show(
                this.translate.instant('GroupClassDetails.CancellationConfirmationTitle'),
                this.translate.instant('GroupClassDetails.SubstitutionCancellationConfirmationText'),
                this.translate.instant('General.Confirm'),
                this.translate.instant('General.Dismiss')
            )
            .subscribe(result => {
                if (result) {
                    this.api.cancelSubstitution(this.model.id).subscribe(() => {
                        this.showOperationSuccess();
                        this.model.substituteInstructor = null;
                    });
                }
            });
    }

    public cancel(): void {
        const bodyText: string = this.model.isRecurring
            ? this.translate.instant('GroupClassDetails.CancellationRecurringConfirmationText')
            : this.translate.instant('GroupClassDetails.CancellationConfirmationText');

        const headerText: string = this.model.isRecurring
            ? this.translate.instant('GroupClassDetails.CancellationHeader')
            : this.translate.instant('GroupClassDetails.CancellationConfirmationTitle');

        const params = {
            event: this.model,
            warningBar: true,
            bodyText: bodyText,
            headerText: headerText,
            confirmed: (includeFollowing: boolean) => {
                this.api.cancel(this.model.id, includeFollowing).subscribe(() => {
                    this.showOperationSuccess();
                    this.model.status = gcManagementEnumerations.GroupClassStatus.cancelled;
                });
            }
        };

        this.modalService
            .create<RecurringClassActionConfirmationComponent>(
                RecurringClassActionConfirmationComponent,
                params,
                this.moduleRef,
                '#confirmationModal'
            )
            .subscribe(() => {});
    }

    public restore(): void {
        const messageKey =
            'GroupClassDetails.' +
            (this.model.hasAlreadyStarted ? 'RestorePastClassConfirmationText' : 'RestoreRegularConfirmationText');

        this.confirmationService
            .show(
                this.translate.instant('GroupClassDetails.RestoreConfirmationTitle'),
                this.translate.instant(messageKey),
                this.translate.instant('General.Confirm'),
                this.translate.instant('General.Dismiss')
            )
            .subscribe(decision => {
                if (decision) {
                    this.api.restore(this.model.id).subscribe(() => {
                        this.showOperationSuccess();
                        this.model.status = gcManagementEnumerations.GroupClassStatus.active;
                    });
                }
            });
    }

    public requestSubstitution(): void {
        this.subBoardApi.requestSubstitution(this.model.club.id, this.model.id).subscribe(() => {
            this.showOperationSuccess();
        });
    }

    public delete(): void {
        const clubId = this.model.club.id;

        this.removeClassService.removeClass(this.model, () => setTimeout(() => this.location.back(), 1000), clubId);
    }

    public showItemHistory() {
        this.auditLogService.showAuditLog(this.model.id);
    }

    private showOperationSuccess() {
        this.toast.success(this.translate.instant('Utils.OperationOk'));
    }
    public get isActive(): boolean {
        return this.model.status.code === gcManagementEnumerations.GroupClassStatus.active.code;
    }

    public get isCancelled(): boolean {
        return this.model.status.code === gcManagementEnumerations.GroupClassStatus.cancelled.code;
    }

    /**
     * isDeleteAvailable
     */
    public get isDeleteAvailable() {
        return !this.cannotDeletePermanent;
    }

    public get isBookingWindowValueIsPlural(): boolean {
        return this.model.bookingWindowValue > 1;
    }

    public get isBookingWindowUnitIsDays(): boolean {
        return this.model.bookingWindowUnit.code === gcManagementEnumerations.GroupClassBookingWindowUnit.days.code;
    }

    public get canSubstitute(): boolean {
        return this.isActive && this.model.instructor != null && this.model.substituteInstructor == null;
    }

    public get canCreateSubRequest(): boolean {
        return this.isActive && !this.classIsFromThePast &&
        (this.model.instructor != null || this.model.substituteInstructor != null);
    }

    public get canCancelSubstitution(): boolean {
        return this.isActive && this.model.substituteInstructor != null;
    }

    public get canShowItemHistory(): boolean {
        return true;
    }

    public get classIsFromThePast(): boolean {
        const nowInTimezone = this.timeService.nowInTimeZone(this.model.club.timezone);
        const m = this.timeService.toMoment(this.model.date);
        m.add(this.model.timeToInMinutes, 'minutes');
        return m.isBefore(nowInTimezone);
    }
}
